import React, { useMemo, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/styles";
import { Container, Typography, Box, Grid, useMediaQuery } from "@mui/material";
import sh from "bundles/common/utils/sh";
import JobOfferList from "bundles/Candidate/components/JobOfferList";
import {
  ARCHITECTURE_CATEGORIES,
  ENGINEERING_CATEGORIES,
  CONSTRUCTION_CATEGORIES,
  ADMINISTRATION_CATEGORIES,
} from "bundles/common/utils/categoryData.js";
import {
  getEnglishSlug,
} from "bundles/common/utils/urlTranslations";
import BreadcrumbsBar from "./BreadcrumbsBar";
import NoJobsAvailable from "./NoJobsAvailable";

const CategoryIndex = () => {
  const { industry, category: catString = "" } = useParams();
  const navigate = useNavigate();
  const i18nLocale = useSelector(state => state.i18nLocale);
  const authenticityToken = useSelector(state => state.authenticity_token);
  const intl = useIntl();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [jobOffers, setJobOffers] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);

  const catArray = catString.split(",").map(s => s.trim()).filter(Boolean);
  const englishCats = catArray.map(slug =>
    getEnglishSlug(industry, slug, i18nLocale)
  );
  const [categoryLabel, setCategoryLabel] = useState(catString);
  const [loading, setLoading] = useState(true);

  const fetchOffers = () => {
    sh.get(`/${i18nLocale}/job-offers/fetch`, {
      params: {
        authenticity_token: authenticityToken,
        page,
        industry,
        category: englishCats.join(","),
      },
    })
      .then(res => {
        const offers = res.data?.data || [];
        setJobOffers(offers);
        setPageCount(res.data?.pagy?.pages || 1);
        setLoading(false);
        if (!selectedJob && offers.length > 0) {
          setSelectedJob(offers[0]);
        }
      })
      .catch(err => console.error(err));
  };

  useEffect(() => {
    fetchOffers();
  }, [page, industry, catString]);

  useEffect(() => {
    let arrayForIndustry = [];
    switch (industry) {
      case "architecture":
        arrayForIndustry =
          ARCHITECTURE_CATEGORIES[i18nLocale] || ARCHITECTURE_CATEGORIES["en"];
        break;
      case "engineering":
        arrayForIndustry =
          ENGINEERING_CATEGORIES[i18nLocale] || ENGINEERING_CATEGORIES["en"];
        break;
      case "construction":
        arrayForIndustry =
          CONSTRUCTION_CATEGORIES[i18nLocale] || CONSTRUCTION_CATEGORIES["en"];
        break;
      case "administration":
        arrayForIndustry =
          ADMINISTRATION_CATEGORIES[i18nLocale] ||
          ADMINISTRATION_CATEGORIES["en"];
        break;
      default:
        arrayForIndustry = [];
        break;
    }

    const found = arrayForIndustry.find(c => c.slug === catString);
    if (found) {
      setCategoryLabel(found.label);
    } else {
      setCategoryLabel(catString);
    }
  }, [industry, catString, i18nLocale]);

  const crumbs = useMemo(() => [
    { label: "Home",       path: `/${i18nLocale}` },
    { label: "Job Offers", path: `/${i18nLocale}/job-offers` },
    {
      label: industry.charAt(0).toUpperCase() + industry.slice(1),
      path: `/${i18nLocale}/job-offers/${industry}`,
    },
    {
      label: categoryLabel,
      path: `/${i18nLocale}/job-offers/${industry}/${catString}`,
      current: true,
    },
  ], [industry, catString, categoryLabel, i18nLocale]);


  const handleOfferClick = offer => {
    if (offer) {
      navigate(`/${i18nLocale}/job-offers/${offer.id}`, { state: { crumbs } });
    }
  };

  if (!loading && jobOffers.length === 0) {
    return <NoJobsAvailable />;
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 5 }}>
      <Box textAlign="center" mb={4}>
        <Typography variant="h1">
          {intl.formatMessage(
            { id: "public_job_offer_index.category_index_header" },
            { category: categoryLabel }
          )}
        </Typography>
      </Box>

      <Box>
        <BreadcrumbsBar crumbs={crumbs} />
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <JobOfferList
            offers={jobOffers}
            activeOffer={selectedJob}
            handleOfferClick={handleOfferClick}
            columnPerRow={isDesktop ? 2 : 1}
            page={page}
            setPage={setPage}
            pageCount={pageCount}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default CategoryIndex;
